import React, { useState } from "react";
import {
  Container,
  Typography,
  Stack,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  CircularProgress,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { loadStripe } from "@stripe/stripe-js";
import { useCreateSubscription } from "../../generated/hook";

loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || "");

const UpgradePlanPage: React.FC = () => {
  const { mutateAsync: createSubscription, error: apiError } =
    useCreateSubscription();
  const [uiMessage] = useState<string | null>(null);
  const [isLoadingClientSecret] = useState(false);

  const tiers = [
    {
      title: "Free Tier",
      price: "0",
      description: ["Register habit entries manually over a year"],
      buttonText: "Current Plan",
      buttonVariant: "outlined" as "outlined" | "contained",
      linkTo: "#", // No action or link to dashboard
      disabled: true, // Disable as user is on upgrade page, implying they are on free tier
    },
    {
      title: "Plus Tier",
      price: "10",
      priceId: "price_1RXJ4AFpXSJuaxQVOQnIkL3m",
      subheader: "per year",
      description: [
        "All Free Tier features",
        "View insightful graphics & track progress",
        "Set email reminders for your habits",
        "Receive personalized advice",
        "Exclusive members-only newsletter",
      ],
      buttonText: "Upgrade to Plus",
      buttonVariant: "contained" as "outlined" | "contained",
    },
    {
      title: "Family Tier",
      price: "20",
      priceId: "price_1RXJ4sFpXSJuaxQVxHmRJZTV",
      subheader: "per year",
      description: [
        "All Plus Tier features",
        "Share habits with up to 5 family members",
      ],
      buttonText: "Upgrade to Family",
      buttonVariant: "contained" as "outlined" | "contained",
    },
  ];

  const onClickUpgrade = async (tier: (typeof tiers)[number]) => {
    if (tier.disabled) return; // Prevent action if tier is disabled

    if (!tier.priceId) return;

    try {
      const data = await createSubscription({
        data: { priceId: tier.priceId },
      });
      const session = data;

      window.location.href = session.stripeSessionUrl;
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <Typography variant="h3" align="center" color="text.primary" gutterBottom>
        Upgrade Your Plan
      </Typography>
      <Typography
        variant="h6"
        align="center"
        color="text.secondary"
        component="p"
        sx={{ mb: 6 }}
      >
        Unlock powerful features and take your habit tracking to the next level
        with our Plus plan.
      </Typography>
      {(apiError || uiMessage) && (
        <Typography color="error" align="center" sx={{ mb: 2 }}>
          {/* Display UI message first if available, otherwise fallback to API error */}
          {uiMessage || ""}
        </Typography>
      )}
      {/* This block is for general UI messages, primarily errors before payment form is shown */}
      {uiMessage && (
        <Typography color="error" align="center" sx={{ mb: 2 }}>
          {uiMessage}
        </Typography>
      )}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={5}
        alignItems="flex-end"
        justifyContent="center"
        style={{ display: "flex" }} // Hide tiers when payment form is shown
        sx={{ width: "100%" }}
      >
        {tiers.map((tier) => (
          <Box
            key={tier.title}
            sx={{
              width: { xs: "100%" },
              flexGrow: { md: 1 },
              flexBasis: { md: 0 },
              minWidth: { md: "300px" },
            }}
          >
            <Card
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardHeader
                title={tier.title}
                subheader={tier.subheader}
                titleTypographyProps={{ align: "center" }}
                subheaderTypographyProps={{
                  align: "center",
                }}
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === "light"
                      ? theme.palette.grey[200]
                      : theme.palette.grey[700],
                }}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "baseline",
                    mb: 2,
                  }}
                >
                  <Typography component="h2" variant="h3" color="text.primary">
                    €{tier.price}
                  </Typography>
                  {tier.price !== "0" && tier.subheader && (
                    <Typography variant="h6" color="text.secondary">
                      &nbsp;/{tier.subheader.split(" ")[1]}
                    </Typography>
                  )}
                </Box>
                <List>
                  {tier.description.map((line) => (
                    <ListItem key={line} disableGutters>
                      <ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>
                        <CheckIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText primary={line} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <CardActions>
                <Button
                  fullWidth
                  variant={tier.buttonVariant}
                  onClick={() => {
                    onClickUpgrade(tier);
                    // navigate("/auth/signin");
                  }}
                  disabled={isLoadingClientSecret}
                  sx={{ position: "relative" }}
                >
                  {isLoadingClientSecret && tier.title === "Plus Tier" && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-12px",
                        marginLeft: "-12px",
                      }}
                    />
                  )}
                  {tier.buttonText}
                </Button>
              </CardActions>
            </Card>
          </Box>
        ))}
      </Stack>
    </Container>
  );
  // ) : (
  //   <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
  //     <Typography variant="h4" align="center" gutterBottom>
  //       Complete Your Payment
  //     </Typography>
  //     {/* Display messages related to payment processing */}
  //     {/* {uiMessage && (
  //       <Typography
  //         color={typeof uiMessage === 'string' && uiMessage.toLowerCase().includes('success') ? "success" : "error"}
  //         align="center"
  //         sx={{ mb: 2 }}
  //       >
  //         {uiMessage}
  //       </Typography>
  //     )} */}
  //     {typeof clientSecret === 'string' && stripePromise && (
  //       <Elements stripe={stripePromise} options={{ clientSecret }}>
  //         <CheckoutForm
  //           clientSecret={clientSecret}
  //           onPaymentSuccess={() => {
  //             setUiMessage('Payment successful! Redirecting to dashboard...');
  //             setTimeout(() => navigate('/dashboard?subscription=success'), 2000);
  //           }}
  //           onPaymentError={(paymentErrorMessage: string) => setUiMessage(paymentErrorMessage)}
  //         />
  //       </Elements>
  //     )}
  //     <Button
  //       onClick={() => {
  //         setClientSecret(null);
  //         setUiMessage(null); // Clear any messages when cancelling
  //       }}
  //       fullWidth
  //       variant="outlined"
  //       sx={{ mt: 2 }}
  //       disabled={isLoadingClientSecret}
  //     >
  //       Cancel Payment
  //     </Button>
  //   </Container>
  // ); // This closes the 'else' block for clientSecret
};

export default UpgradePlanPage;
